import { Slot } from "@radix-ui/react-slot";
import { cond, isEqual, isFunction, stubTrue } from "lodash";
import type { ReactElement, ReactNode } from "react";
import { isValidElement } from "react";
import { Children } from "react";

type Tuple<T1, T2> = [T1, T2];

type Props<T = unknown> = {
  match: T;
  asChild?: boolean;
  children: [...ReactElement<CaseProps>[], ReactElement<OtherwiseProps>];
};

const Match = <T extends unknown>({ match, asChild, children }: Props<T>) => {
  const Comp = asChild ? Slot : "div";

  const childArray = Children.toArray(children);
  const conditions = childArray.flatMap((child, index) => {
    if (!isValidElement(child)) {
      return [];
    }

    if (child.type !== Case && child.type !== Otherwise) {
      throw new Error(
        "Match component only accepts Case and Otherwise components.",
      );
    }

    if (child.type === Case) {
      const predicate = isFunction(child.props.predicate)
        ? child.props.predicate
        : curriedEqual(child.props.predicate);
      return [[predicate, () => child.props.children]];
    }

    /**
     * @description 마지막 요소로 Otherwise가 쓰이지 않은 경우 경고를 출력
     */
    if (index !== childArray.length - 1) {
      console.warn(
        "Otherwise component should be last element in the children array.",
      );
    }

    return [[stubTrue, () => child.props.children]];
  }) as Tuple<(args: unknown) => boolean, () => ReactNode>[];

  const result = cond(conditions)(match);

  return <Comp>{result}</Comp>;
};

const curriedEqual = (a: unknown) => (b: unknown) => isEqual(a, b);

export default Match;

type CaseProps<T = unknown> = {
  children: ReactNode;
  predicate: T | ((match: T) => boolean);
};
export const Case = <T extends unknown>({ children }: CaseProps<T>) => {
  return <>{children}</>;
};

type OtherwiseProps = {
  children: ReactNode;
};
export const Otherwise = ({ children }: OtherwiseProps) => {
  return <>{children}</>;
};

Match.Case = Case;
Match.Otherwise = Otherwise;
